import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA4 from 'react-ga4';
import clsx from 'clsx';
import { useSwitch, useUserType } from '@/hooks';
import { IconRcArrowDown } from '@/assets';
import { IconRcI18n } from '@/assets/sidebar';
import { Button, Checkbox, Modal } from '@/components';
import { SUPPORTED_LOCALES } from '@/locales';
import { useLocale, useAutoTranslateUGC } from '@/utils/i18n';
import style from './index.module.scss';

const { CommonModal } = Modal;

export const I18nSwitch = () => {
  const userType = useUserType();
  const [currentLocale] = useLocale();
  const { on, turnOn, turnOff } = useSwitch();

  const handleIconClick = useCallback(() => {
    ReactGA4.event({
      category: 'AI Language',
      action: `Click_Language_Icon_${userType}`,
    });
    turnOn();
  }, [turnOn, userType]);

  const localeLable = useMemo(
    () => SUPPORTED_LOCALES.find(x => x.key === currentLocale)?.abbr,
    [currentLocale],
  );

  return (
    <>
      <div className={style.i18nSwitch} onClick={handleIconClick}>
        {currentLocale ? <span className={style.currentLable}>{localeLable}</span> : ''}
        <IconRcI18n className={style.i18nIcon} />
        <IconRcArrowDown />
      </div>
      {on ? <I18nSwitchModal turnOff={turnOff} /> : null}
    </>
  );
};

export const I18nSwitchModal = ({ turnOff }: { turnOff: () => void }) => {
  const { t } = useTranslation();
  const userType = useUserType();
  const [currentLocale, setCurrentLocale] = useLocale();
  const { autoTranslateUGC, setAutoTranslateUGC } = useAutoTranslateUGC();
  const [selectedValue, setSelectedValue] = useState<null | string>(currentLocale);
  const [checked, setChecked] = useState(!!autoTranslateUGC);
  const [isCheckBoxDisable, setCheckBoxDisable] = useState(false);

  useEffect(() => {
    if (!selectedValue) {
      setChecked(false);
      setCheckBoxDisable(true);
    } else {
      setCheckBoxDisable(false);
    }
  }, [selectedValue]);
  const handleLocaleClick = useCallback(
    (l: string) => {
      if (l === selectedValue) {
        setSelectedValue('');
        return;
      }
      setSelectedValue(l);
    },
    [selectedValue],
  );

  const handleConfirmClick = useCallback(async () => {
    if (selectedValue === currentLocale && autoTranslateUGC === checked) {
      turnOff();
      return;
    }
    if (selectedValue) {
      const enAbbr = SUPPORTED_LOCALES.find(x => x.key === selectedValue)?.enAbbr;
      ReactGA4.event({
        category: 'AI Language',
        action: `Choose_Language_${enAbbr}_${userType}`,
      });
    }
    setCurrentLocale(selectedValue);
    setAutoTranslateUGC(checked);

    turnOff();
  }, [
    selectedValue,
    currentLocale,
    autoTranslateUGC,
    checked,
    setCurrentLocale,
    setAutoTranslateUGC,
    turnOff,
    userType,
  ]);

  const handleCheckBox = useCallback(e => {
    setChecked(e.target.checked);
  }, []);

  return (
    <CommonModal onRequestClose={turnOff} containerClassName={style.container}>
      <div className={style.title}>{t('I18nSwitch.19ewry0')}</div>
      <div className={style.listContainer}>
        <div className={style.listTitle}>{t('I18nSwitch.prefer')}</div>
        <div className={style.list}>
          {SUPPORTED_LOCALES?.filter(i => !!i?.key)?.map(x => (
            <div
              key={x.key}
              className={clsx(style.locale, selectedValue === x.key && style.isSelected)}
              onClick={() => handleLocaleClick(x.key)}
              data-l={x.key}>
              {x.label}
            </div>
          ))}
        </div>
      </div>
      <Checkbox
        type="form"
        className={style.checkBox}
        checked={checked}
        onChange={handleCheckBox}
        disabled={isCheckBoxDisable}
        tooltipText={isCheckBoxDisable ? t('I18nSwitch.disabledTip') : undefined}>
        {t('I18nSwitch.autoTrans')}
      </Checkbox>
      <div className={style.btnContainer}>
        <Button className={clsx(style.btn, style.cancelBtn)} onClick={() => turnOff()}>
          {t('I18nSwitch.cancel')}
        </Button>
        <Button type="primary" className={style.btn} onClick={handleConfirmClick}>
          {t('I18nSwitch.done')}
        </Button>
      </div>
    </CommonModal>
  );
};
